import React, { useState } from 'react';
import {
  Stack,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { reportApi } from '../../../services/reportService';
import { StatTile, PersonAvatar } from '../../../components/ui';
import { tokens } from '../../../theme';
import { DateRange } from './StatsDateRange';
import {
  SectionCard,
  formatPLN,
  formatNumber,
  formatPct,
  useAsyncData,
  StatsLoader,
  EmptyHint,
  RankList,
  StatTileGrid,
  StatsRow,
} from './statsShared';
import { RevenueAreaChart } from './ChartKit';

interface Props {
  salonId: string;
  range: DateRange;
}

const NUM = { fontVariantNumeric: 'tabular-nums' } as const;

// Kolor paska to stan: od 70% dobrze, 40–69% uwaga, ponizej 40% niskie oblozenie.
const occupancyColor = (value: number) => (value >= 70 ? tokens.success : value >= 40 ? tokens.warning : tokens.danger);

const OccupancyBar: React.FC<{ value: number }> = ({ value }) => (
  <Stack direction="row" alignItems="center" gap={1.25}>
    <Box sx={{ flex: 1, minWidth: 48, height: 6, borderRadius: 99, bgcolor: tokens.surfaceStrong, overflow: 'hidden' }}>
      <Box sx={{ height: '100%', width: `${Math.max(0, Math.min(100, value))}%`, bgcolor: occupancyColor(value), borderRadius: 99 }} />
    </Box>
    <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, width: 42, textAlign: 'right', ...NUM }}>{formatPct(value)}</Typography>
  </Stack>
);

const MiniStat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted, fontWeight: 600 }}>{label}</Typography>
    <Typography sx={{ fontSize: '1.125rem', fontWeight: 750, ...NUM }}>{value}</Typography>
  </Box>
);

const EmployeeDialog: React.FC<{ salonId: string; staffId: string; range: DateRange; onClose: () => void }> = ({
  salonId,
  staffId,
  range,
  onClose,
}) => {
  const { data, loading, error } = useAsyncData(
    () => reportApi.getEmployeeStats(salonId, staffId, range.dateFrom, range.dateTo),
    [salonId, staffId, range.dateFrom, range.dateTo],
  );
  const maxServiceRevenue = Math.max(1, ...(data?.services ?? []).map((s) => s.revenue));

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth aria-labelledby="employee-stats-title">
      <DialogTitle id="employee-stats-title" sx={{ pr: 7 }}>
        <Stack direction="row" alignItems="center" gap={1.5}>
          {data && <PersonAvatar firstName={data.firstName} lastName={data.lastName} seed={data.id} size={40} />}
          <Box sx={{ minWidth: 0 }}>
            {data ? `${data.firstName} ${data.lastName}` : 'Statystyki pracownika'}
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: tokens.muted }}>Wyniki w wybranym okresie</Typography>
          </Box>
        </Stack>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 14, top: 18 }} aria-label="Zamknij">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: tokens.ground, pt: 2.5, pb: 3 }}>
        <StatsLoader loading={loading} error={error} hasData={!!data}>
          {data && (
            <Stack gap={2}>
              <StatTileGrid>
                <StatTile label="Przychód" value={formatPLN(data.revenue)} />
                <StatTile label="Wizyty" value={formatNumber(data.bookings)} />
                <StatTile label="Klienci" value={formatNumber(data.clientsServed)} />
                <StatTile label="Szacowana prowizja" value={formatPLN(data.estCommission)} />
              </StatTileGrid>

              <StatsRow template="7fr 5fr">
                <SectionCard title="Przychód w czasie">
                  {data.timeseries.some((t) => t.revenue > 0) ? (
                    <RevenueAreaChart data={data.timeseries.map((t) => ({ label: t.label, revenue: t.revenue }))} height={220} />
                  ) : (
                    <EmptyHint>Brak przychodu w wybranym okresie</EmptyHint>
                  )}
                </SectionCard>
                <SectionCard title="Obłożenie i statusy">
                  <Stack gap={2.5}>
                    <Box>
                      <Typography sx={{ fontSize: '0.875rem', color: tokens.inkSoft, mb: 0.75 }}>
                        Obłożenie według grafiku ({formatNumber(data.scheduledHours)} h)
                      </Typography>
                      <OccupancyBar value={data.occupancy} />
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1.5 }}>
                      <MiniStat label="Zakończone" value={formatNumber(data.completed)} />
                      <MiniStat label="Anulowane" value={formatNumber(data.cancelled)} />
                      <MiniStat label="Nieobecności" value={formatNumber(data.noShow)} />
                    </Box>
                  </Stack>
                </SectionCard>
              </StatsRow>

              <SectionCard title="Usługi" subtitle="Według przychodu">
                {data.services.length ? (
                  <RankList
                    items={data.services.slice(0, 8).map((s) => ({
                      id: s.id,
                      label: s.name,
                      sub: `${formatNumber(s.count)}×`,
                      value: formatPLN(s.revenue),
                      ratio: s.revenue / maxServiceRevenue,
                    }))}
                  />
                ) : (
                  <EmptyHint>Brak usług w wybranym okresie</EmptyHint>
                )}
              </SectionCard>
            </Stack>
          )}
        </StatsLoader>
      </DialogContent>
    </Dialog>
  );
};

export const EmployeesTab: React.FC<Props> = ({ salonId, range }) => {
  const { data, loading, error } = useAsyncData(
    () => reportApi.getEmployees(salonId, range.dateFrom, range.dateTo),
    [salonId, range.dateFrom, range.dateTo],
  );
  const [selected, setSelected] = useState<string | null>(null);

  const totalRevenue = (data ?? []).reduce((s, e) => s + e.revenue, 0);
  const avgOccupancy = data && data.length ? Math.round(data.reduce((s, e) => s + e.occupancy, 0) / data.length) : 0;
  const topEarner = data && data.length ? data[0] : null;

  return (
    <StatsLoader loading={loading} error={error} hasData={!!data}>
      {data && (
        <Stack gap={2}>
          <StatTileGrid>
            <StatTile label="Pracownicy" value={formatNumber(data.length)} />
            <StatTile label="Przychód zespołu" value={formatPLN(totalRevenue)} />
            <StatTile label="Średnie obłożenie" value={formatPct(avgOccupancy)} progress={avgOccupancy} />
            <StatTile
              label="Najwyższy przychód"
              value={topEarner ? formatPLN(topEarner.revenue) : '—'}
              hint={topEarner ? `${topEarner.firstName} ${topEarner.lastName}` : undefined}
            />
          </StatTileGrid>

          <SectionCard title="Pracownicy" subtitle="Kliknij wiersz, aby zobaczyć szczegóły pracownika" padding={data.length ? 'none' : 'normal'}>
            {data.length ? (
              <TableContainer>
                <Table sx={{ minWidth: 720 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Pracownik</TableCell>
                      <TableCell align="right">Wizyty</TableCell>
                      <TableCell align="right">Klienci</TableCell>
                      <TableCell align="right">Przychód</TableCell>
                      <TableCell align="right">Prowizja</TableCell>
                      <TableCell sx={{ width: 200 }}>Obłożenie</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.map((e) => (
                      <TableRow
                        key={e.id}
                        hover
                        tabIndex={0}
                        sx={{ cursor: 'pointer', '&:focus-visible': { outline: `2px solid ${tokens.plum}`, outlineOffset: -2 } }}
                        onClick={() => setSelected(e.id)}
                        onKeyDown={(ev) => {
                          if (ev.key === 'Enter' || ev.key === ' ') {
                            ev.preventDefault();
                            setSelected(e.id);
                          }
                        }}
                      >
                        <TableCell>
                          <Stack direction="row" alignItems="center" gap={1.25}>
                            <PersonAvatar firstName={e.firstName} lastName={e.lastName} seed={e.id} size={32} />
                            <Box component="span" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                              {e.firstName} {e.lastName}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell align="right" sx={NUM}>{formatNumber(e.bookings)}</TableCell>
                        <TableCell align="right" sx={NUM}>{formatNumber(e.clientsServed)}</TableCell>
                        <TableCell align="right" sx={{ ...NUM, fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {formatPLN(e.revenue)}
                        </TableCell>
                        <TableCell align="right" sx={{ ...NUM, whiteSpace: 'nowrap' }}>{formatPLN(e.estCommission)}</TableCell>
                        <TableCell>
                          <OccupancyBar value={e.occupancy} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <EmptyHint description="Dodaj pracowników w module Zespół, żeby zobaczyć ich wyniki.">Brak aktywnych pracowników</EmptyHint>
            )}
          </SectionCard>
        </Stack>
      )}

      {selected && <EmployeeDialog salonId={salonId} staffId={selected} range={range} onClose={() => setSelected(null)} />}
    </StatsLoader>
  );
};
